import { useState } from "react";
import { useVoiceSettings } from "../useVoiceSettings";
import EndOfPhraseDetection, {
  EndOfPhraseMode,
  getEndOfPhraseMode,
} from "./EndOfPhraseDetection";
import SpeechActivityDetection from "./SpeechActivityDetection";

enum InterruptionMode {
  VAD,
  END_OF_PHRASE_DETECTION,
  AUDIO_END_OF_PHRASE_DETECTION,
}

export default function InterruptionSettings(props) {
  const {
    interruption,
    conversationControlSettings,
    workspaceId,
    assistantId,
    syncAssistant,
    locked
  } = props;

  const { updateVoiceSettings } = useVoiceSettings({
    syncAssistant,
    workspaceId,
    assistantId,
  });

  const [settings, setSettings] = useState({
    interruption,
    conversationControlSettings,
  });

  const [errors, setErrors] = useState({
    transcriptionReleaseTimeout: null,
  });

  function handleUpdateError(modifiedError) {
    setErrors((prevErrors) => {
      return {
        ...prevErrors,
        ...modifiedError,
      };
    });
  }

  function handleToggleBetweenVadInterruptionAndEndOfPhraseDetection(
    mode: InterruptionMode
  ) {
    const isVad = mode === InterruptionMode.VAD;
    const isEndOfPhraseDetection =
      mode === InterruptionMode.END_OF_PHRASE_DETECTION;
    const isAudioEndOfPhraseDetection =
      mode === InterruptionMode.AUDIO_END_OF_PHRASE_DETECTION;

    setSettings((prevSettings) => {
      const prevInterruptionSettings = prevSettings.interruption;
      const prevConversationControlSettings =
        prevSettings.conversationControlSettings;

      // Base case preserved verbatim: VAD on turns semantic EOP off.
      // Extended for three-way exclusivity: it also turns audio EOP off.
      if (isVad && !prevInterruptionSettings.vadInterruptionEnabled) {
        return {
          interruption: {
            ...prevInterruptionSettings,
            vadInterruptionEnabled: true,
          },
          conversationControlSettings: {
            ...prevConversationControlSettings,
            endOfPhraseDetectionEnabled: false,
            audioEndOfPhraseDetectionEnabled: false,
          },
        };
      }

      // Base case preserved verbatim: semantic EOP on turns VAD off.
      // Extended for three-way exclusivity: it also turns audio EOP off.
      if (
        isEndOfPhraseDetection &&
        !prevConversationControlSettings.endOfPhraseDetectionEnabled
      ) {
        return {
          interruption: {
            ...prevInterruptionSettings,
            vadInterruptionEnabled: false,
          },
          conversationControlSettings: {
            ...prevConversationControlSettings,
            endOfPhraseDetectionEnabled: true,
            audioEndOfPhraseDetectionEnabled: false,
          },
        };
      }

      // New third mode, mirrors the two branches above: audio EOP on turns
      // both VAD and semantic EOP off.
      if (
        isAudioEndOfPhraseDetection &&
        !prevConversationControlSettings.audioEndOfPhraseDetectionEnabled
      ) {
        return {
          interruption: {
            ...prevInterruptionSettings,
            vadInterruptionEnabled: false,
          },
          conversationControlSettings: {
            ...prevConversationControlSettings,
            endOfPhraseDetectionEnabled: false,
            audioEndOfPhraseDetectionEnabled: true,
          },
        };
      }

      // Toggling the currently-enabled mode off (or any other case): all
      // three modes end up off, same as the original two-mode "else".
      return {
        interruption: {
          ...prevInterruptionSettings,
          vadInterruptionEnabled: false,
        },
        conversationControlSettings: {
          ...prevConversationControlSettings,
          endOfPhraseDetectionEnabled: false,
          audioEndOfPhraseDetectionEnabled: false,
        },
      };
    });
  }

  // Mode-change handler for the End Of Phrase Detection card's three-way
  // radio selector (Off / Semantic / Audio). Kept separate from
  // handleToggleBetweenVadInterruptionAndEndOfPhraseDetection above (which
  // remains exactly as-is and still backs the Speech Activity Detection
  // card's VAD switch) because "Off" has different exclusivity semantics:
  // choosing Off must NOT touch vadInterruptionEnabled -- VAD and "no EOP
  // mode" are not mutually exclusive, only Semantic/Audio EOP are mutually
  // exclusive with VAD.
  function handleEndOfPhraseModeChange(mode: EndOfPhraseMode) {
    setSettings((prevSettings) => {
      const prevInterruptionSettings = prevSettings.interruption;
      const prevConversationControlSettings =
        prevSettings.conversationControlSettings;

      // Same-mode reselection guard: the whole radio card is clickable and
      // the Radio circle's own onClick still fires (plus the group's
      // keyboard onChange), so one interaction can invoke this handler more
      // than once with the same mode. Returning the previous state object
      // unchanged makes reselection a strict no-op (React bails out of the
      // re-render on identical state reference).
      if (getEndOfPhraseMode(prevConversationControlSettings) === mode) {
        return prevSettings;
      }

      if (mode === EndOfPhraseMode.SEMANTIC) {
        return {
          interruption: {
            ...prevInterruptionSettings,
            vadInterruptionEnabled: false,
          },
          conversationControlSettings: {
            ...prevConversationControlSettings,
            endOfPhraseDetectionEnabled: true,
            audioEndOfPhraseDetectionEnabled: false,
          },
        };
      }

      if (mode === EndOfPhraseMode.AUDIO) {
        return {
          interruption: {
            ...prevInterruptionSettings,
            vadInterruptionEnabled: false,
          },
          conversationControlSettings: {
            ...prevConversationControlSettings,
            endOfPhraseDetectionEnabled: false,
            audioEndOfPhraseDetectionEnabled: true,
          },
        };
      }

      // mode === EndOfPhraseMode.OFF: turn both EOP flags off; leave VAD
      // untouched since Off + VAD-on is a valid, non-exclusive combination.
      return {
        interruption: prevInterruptionSettings,
        conversationControlSettings: {
          ...prevConversationControlSettings,
          endOfPhraseDetectionEnabled: false,
          audioEndOfPhraseDetectionEnabled: false,
        },
      };
    });
  }

  function handleUpdateInterruption(modifiedSetting) {
    setSettings((prevSettings) => {
      return {
        ...prevSettings,
        interruption: {
          ...prevSettings.interruption,
          ...modifiedSetting,
        },
      };
    });
  }

  function handleUpdateConversationControlSettings(modifiedSetting) {
    setSettings((prevSettings) => {
      return {
        ...prevSettings,
        conversationControlSettings: {
          ...prevSettings.conversationControlSettings,
          ...modifiedSetting,
        },
      };
    });
  }

  function handleSave() {
    if (Boolean(errors?.transcriptionReleaseTimeout)) {
      const { conversationControlSettings, interruption } = settings;

      const updatedSettings = {
        interruption,
        conversationControlSettings: {
          ...conversationControlSettings,
          transcriptionReleaseTimeout: 1000,
        },
      };

      setSettings(updatedSettings);
      setErrors({
        transcriptionReleaseTimeout: null,
      });
      updateVoiceSettings(updatedSettings);
      return;
    }

    updateVoiceSettings(settings);
  }

  return (
    <div className="flex flex-col w-3/5 min-w-[600px] gap-y-8 m-4">
      <EndOfPhraseDetection
        onUpdateConversationControlSettings={
          handleUpdateConversationControlSettings
        }
        onSave={handleSave}
        onModeChange={handleEndOfPhraseModeChange}
        settings={settings}
        errors={errors}
        onError={handleUpdateError}
        locked={locked}
      />
      <SpeechActivityDetection
        onUpdateInterruption={handleUpdateInterruption}
        settings={settings}
        onSave={handleSave}
        onToggleBetweenVadInterruptionAndEndOfPhraseDetection={
          handleToggleBetweenVadInterruptionAndEndOfPhraseDetection
        }
        locked={locked}
      />
    </div>
  );
}
